import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ScanField } from "@/components/ScanField";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { SoundToggle } from "@/components/SoundToggle";
import { toast } from "sonner";
import { ScanLine, Package, MapPin, Truck, ArrowLeft } from "lucide-react";

const KIND = {
  part: { icon: Package, label: "Part" },
  location: { icon: MapPin, label: "Location" },
  donor: { icon: Truck, label: "Truck / Equipment" },
};

// Read-only universal lookup: scan any tag → jump to its record. Separate from the
// capture/put-away action scanners so a lookup can NEVER change data.
export default function QuickScanPage() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const onScan = async (code) => {
    setBusy(true);
    try {
      const { data } = await api.get("/scan/resolve", { params: { code } });
      scanAccept(data.label || "FOUND");
      const k = KIND[data.kind] || {};
      setLast({ ...data, label: data.label });
      toast.success(`${k.label || "Match"}: ${data.label || ""} — opening…`);
      navigate(data.url);
    } catch (e) {
      scanRefuse(formatApiErrorDetail(e.response?.data?.detail) || "No match for that scan.");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No match for that scan.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="quick-scan-page">
      <button data-testid="quick-scan-back" onClick={() => navigate("/home")} className="flex items-center gap-1.5 font-mono text-xs text-[#B01E28] hover:underline mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> Home
      </button>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="h-1 bg-[#B01E28]" />
        <div className="p-5 sm:p-6">
          <div className="flex items-center justify-between gap-2.5">
            <div className="flex items-center gap-2.5">
              <div className="w-11 h-11 rounded-xl bg-[#B01E28] flex items-center justify-center shrink-0">
                <ScanLine className="w-6 h-6 text-white" strokeWidth={2.2} />
              </div>
              <div>
                <h1 className="font-display text-xl font-black tracking-tight leading-tight">Scan / Lookup</h1>
                <p className="text-xs text-slate-500">Scan any tag to open its record — read-only.</p>
              </div>
            </div>
            <SoundToggle />
          </div>

          <div className="mt-5">
            <ScanField
              testid="quick-scan"
              label="Scan a part, bin, or truck tag"
              placeholder="Scan or type a code…"
              submitLabel="Look up"
              onScan={onScan}
              busy={busy}
            />
          </div>

          {last && (
            <div data-testid="quick-scan-last" className="mt-4 flex items-center gap-2 text-sm text-slate-600 border border-slate-100 bg-slate-50 rounded-lg px-3 py-2">
              {(() => { const I = (KIND[last.kind] || {}).icon || Package; return <I className="w-4 h-4 text-[#B01E28]" />; })()}
              <span className="font-medium">{(KIND[last.kind] || {}).label}</span>
              <span className="font-mono text-xs text-slate-500 truncate">{last.label}</span>
            </div>
          )}
        </div>
      </div>

      <p className="text-[11px] text-slate-400 mt-3 text-center">
        Lookup only — nothing is changed. Use Capture or Put Away to record work.
      </p>
    </div>
  );
}
